import React, { useMemo, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useLocalizedStrings } from '../../../Localization';
import { Card, CardBody, Button, Icon, Modal } from '@aidkitorg/component-library';
import { AdminPanelPage } from '../../AdminPanelPage';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { ApplicantGroupsDataTable, ApplicantGroup } from './ApplicantGroupsDataTable';
import { usePost } from '../../../API';

export const ApplicantGroupsPage: React.FC = () => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const history = useHistory();

  // Fetch applicant groups from the new list endpoint
  const listApplicantGroups = usePost('/api/admin_panel/applicant_groups/list');
  const deleteApplicantGroupAPI = usePost('/api/admin_panel/applicant_groups/delete');
  const [response, setResponse] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ApplicantGroup | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch page configuration from the endpoint
  const fetchPageConfig = usePost('/api/admin_panel/page_config');

  const { data: pageConfigResponse, isLoading: isConfigLoading } = useQuery({
    queryKey: ['adminPanelPageConfig', 'Applicant Groups'],
    queryFn: async () => {
      const response = await fetchPageConfig({
        kind: 'Applicant Groups'
      });
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Extract config from response
  const pageConfig = pageConfigResponse?.data?.config;

  React.useEffect(() => {
    const fetchGroups = async () => {
      try {
        setIsLoading(true);
        const result = await listApplicantGroups({});
        setResponse(result);
      } catch (error) {
        console.error('Error fetching applicant groups:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroups();
  }, []);

  // Transform API response to ApplicantGroup format
  const applicantGroups: ApplicantGroup[] = useMemo(() => {
    if (!response || !response.success || !Array.isArray(response.data)) {
      return [];
    }

    return response.data.map((group: any) => ({
      id: group.id,
      name: group.name,
      description: group.description || '',
      memberCount: group.member_count || 0,
      createdAt: group.created_at,
      updatedAt: group.updated_at,
    }));
  }, [response]);

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.applicants
    },
    {
      label: L.admin_panel.nav.applicant_groups
    }
  ];

  const handleCreateGroup = () => {
    history.push('/admin-panel/applicants/applicant-groups/create');
  };

  const handleCreateFirstGroup = () => {
    history.push('/admin-panel/applicants/applicant-groups/create');
  };

  const handleViewResults = (group: ApplicantGroup) => {
    history.push(`/admin-panel/applicants/applicant-groups/${group.id}`);
  };

  const handleEdit = (group: ApplicantGroup) => {
    history.push(`/admin-panel/applicants/applicant-groups/${group.id}/edit`);
  };

  const handleDeleteClick = (group: ApplicantGroup) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    try {
      const result = await deleteApplicantGroupAPI({ id: groupToDelete.id });
      if (result?.success) {
        setDeleteModalOpen(false);
        setGroupToDelete(null);
        // Refetch the list
        const refreshResult = await listApplicantGroups({});
        setResponse(refreshResult);
      } else {
        console.error('Failed to delete applicant group:', result);
      }
    } catch (error) {
      console.error('Error deleting applicant group:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [groupToDelete, deleteApplicantGroupAPI, listApplicantGroups]);

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  // Use config to determine what to show
  const showCreateGroupButton = pageConfig?.createGroupButton === 'visible';

  const pageActionButtons = showCreateGroupButton ? [
    <Button key="create-group" onClick={handleCreateGroup} icon={<Icon
      name="PlusIcon"
      type="solid"
      role="decorative"
    />}>
      {strings.create_group}
    </Button>
  ] : [];

  return (
    <AdminPanelPage
      title={strings.title}
      description={strings.description}
      breadcrumbs={breadcrumbs}
      pageActionButtons={pageActionButtons}
    >
      {applicantGroups.length === 0 && !isLoading ? (
        <div className="flex flex-col items-center py-16 w-full">
          <h2 className="text-xl font-semibold text-gray-900 mb-0">
            {strings.no_groups_yet}
          </h2>
          <p className="text-gray-600 text-center max-w-md mt-2 mb-4">
            {strings.no_groups_description}
          </p>
          <Button onClick={handleCreateFirstGroup} icon={<Icon name="PlusIcon" type="solid" role="decorative" />}>
            {strings.create_first_group}
          </Button>
        </div>
      ) : (
        <Card className="rounded-md w-100 h-fit">
          <CardBody>
            <ApplicantGroupsDataTable
              groups={applicantGroups}
              onViewResults={handleViewResults}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              isLoading={isLoading}
              pageConfig={pageConfig}
              isConfigLoading={isConfigLoading}
            />
          </CardBody>
        </Card>
      )}
      <Modal
        title={strings.delete_group}
        open={deleteModalOpen}
        onClose={handleCancelDelete}
      >
        <div className="mb-4">
          {strings.delete_confirmation.replace('{groupName}', groupToDelete?.name ?? '')}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            {strings.cancel}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? strings.deleting : strings.delete}
          </Button>
        </div>
      </Modal>
    </AdminPanelPage>
  );
};

export default ApplicantGroupsPage;